// Localização: ./src/components/AIAssistant/ChatInterfaceMini.jsx
// Interface de chat em miniatura para dropdown do header

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAIAssistant } from '../../hooks/useAIAssistant';
import MessageBubble from './MessageBubble';
import { autocompletePacientesUsuarios } from '../../api/api';
import { getPacienteFotoUrl, getUsuarioFotoUrl } from '../../utils/imageUtils';
import { Send, Maximize2, GripVertical, Minus, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Componente de interface de chat em miniatura para dropdown
 * @param {function} onExpand - Callback para expandir para página completa
 * @param {function} onClose - Callback para fechar o dropdown
 */
const ChatInterfaceMini = ({ onExpand = null, onClose = null }) => {
    const { messages, isLoading, sendMessage } = useAIAssistant();
    const [inputValue, setInputValue] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    
    // Estado para controlar interação (hover/foco)
    const [isInteracting, setIsInteracting] = useState(false);
    const containerRef = useRef(null);
    
    // Estado para controlar se está minimizado
    const [isMinimized, setIsMinimized] = useState(() => {
        const saved = localStorage.getItem('ai_chat_minimized');
        return saved === 'true';
    });
    
    // Estados para redimensionamento
    const [isResizing, setIsResizing] = useState(false);
    const [dimensions, setDimensions] = useState(() => {
        // Carregar dimensões salvas do localStorage
        const saved = localStorage.getItem('ai_chat_dimensions');
        if (saved) {
            try {
                const { width, height } = JSON.parse(saved);
                // Validar dimensões (mínimo e máximo)
                const minWidth = 300;
                const maxWidth = Math.min(800, window.innerWidth * 0.9);
                const minHeight = 300;
                const maxHeight = window.innerHeight * 0.9;
                
                if (width >= minWidth && width <= maxWidth && 
                    height >= minHeight && height <= maxHeight) {
                    return { width, height };
                }
            } catch (e) {
                // Se houver erro, usar dimensões padrão
            }
        }
        return { width: null, height: null }; // null = usar dimensões padrão do CSS
    });
    const resizeStartPos = useRef({ x: 0, y: 0, width: 0, height: 0 });
    const resizeAnimationFrameRef = useRef(null);
    const dimensionsRef = useRef(dimensions);
    
    // Sincronizar dimensionsRef com dimensions
    useEffect(() => {
        dimensionsRef.current = dimensions;
    }, [dimensions]);
    
    // Estados para drag (arrastar)
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState(() => {
        // Carregar posição salva do localStorage
        const saved = localStorage.getItem('ai_chat_position');
        if (saved) {
            try {
                const { x, y } = JSON.parse(saved);
                // Validar se a posição ainda é válida (dentro da viewport)
                if (x !== null && y !== null && x >= 0 && y >= 0) {
                    // Verificar se a posição não está muito à direita (máximo 98% da largura da tela)
                    // Considerar largura aproximada do componente (420px ou 90vw)
                    const componentWidth = Math.min(420, window.innerWidth * 0.9);
                    const maxAllowedX = window.innerWidth * 0.98 - componentWidth;
                    if (x <= maxAllowedX && x >= 0) {
                        return { x, y };
                    }
                }
            } catch (e) {
                // Se houver erro, retornar null
            }
        }
        return { x: null, y: null };
    });
    const dragStartPos = useRef({ x: 0, y: 0 });
    const dragOffset = useRef({ x: 0, y: 0 });
    const animationFrameRef = useRef(null);
    const positionRef = useRef(position);
    
    // Sincronizar positionRef com position
    useEffect(() => {
        positionRef.current = position;
    }, [position]);
    
    // Estados para autocomplete
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [cursorPosition, setCursorPosition] = useState(0);
    const suggestionsRef = useRef(null);
    const debounceTimer = useRef(null);
    
    // Scroll automático para a última mensagem
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    useEffect(() => {
        scrollToBottom();
    }, [messages]);
    
    // Foca no input quando o componente monta
    useEffect(() => {
        // Pequeno delay para garantir que o dropdown está aberto
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    }, []);
    
    // Foca no input automaticamente quando a IA termina de responder
    useEffect(() => {
        if (!isLoading && messages.length > 0) {
            // Pequeno delay para garantir que a mensagem foi renderizada
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isLoading, messages.length]);
    
    // Buscar sugestões de autocomplete
    const fetchSuggestions = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.trim().length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        
        try {
            const response = await autocompletePacientesUsuarios(searchTerm);
            const { pacientes = [], usuarios = [] } = response.data || {};
            
            const allSuggestions = [
                ...pacientes.map(p => ({ 
                    ...p, 
                    tipo: 'paciente',
                    fotoUrl: getPacienteFotoUrl(p.fotoURL)
                })),
                ...usuarios.map(u => ({ 
                    ...u, 
                    tipo: 'usuario',
                    fotoUrl: getUsuarioFotoUrl(u.fotoURL)
                }))
            ];
            
            setSuggestions(allSuggestions.slice(0, 5)); // Limitar a 5 sugestões no mini
            setShowSuggestions(allSuggestions.length > 0);
        } catch (error) {
            console.error('Erro ao buscar sugestões:', error);
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }, []);
    
    // Detectar quando o usuário está digitando um nome
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setCursorPosition(e.target.selectionStart);
        
        const cursorPos = e.target.selectionStart;
        const textBeforeCursor = value.substring(0, cursorPos);
        
        const wordMatch = textBeforeCursor.match(/([a-zA-ZÁÉÍÓÚÂÊÔÇáéíóúâêôçãõÀà]{2,})$/);
        
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        
        if (wordMatch && wordMatch[0].length >= 2) {
            const searchTerm = wordMatch[0];
            debounceTimer.current = setTimeout(() => {
                fetchSuggestions(searchTerm);
            }, 300);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };
    
    // Inserir sugestão no input
    const insertSuggestion = (suggestion) => {
        const cursorPos = cursorPosition || inputValue.length;
        const textBeforeCursor = inputValue.substring(0, cursorPos);
        const textAfterCursor = inputValue.substring(cursorPos);
        
        const wordMatch = textBeforeCursor.match(/([a-zA-ZÁÉÍÓÚÂÊÔÇáéíóúâêôçãõÀà]{2,})$/);
        
        if (wordMatch) {
            const wordStart = cursorPos - wordMatch[0].length;
            const newValue = 
                inputValue.substring(0, wordStart) + 
                suggestion.nome_completo + 
                ' ' + 
                textAfterCursor;
            
            setInputValue(newValue);
            setShowSuggestions(false);
            setSuggestions([]);
            setSelectedIndex(-1);
            
            setTimeout(() => {
                inputRef.current?.focus();
                if (inputRef.current) {
                    const newCursorPos = wordStart + suggestion.nome_completo.length + 1;
                    inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                }
            }, 0);
        } else {
            setInputValue(inputValue + suggestion.nome_completo + ' ');
            setShowSuggestions(false);
            setSuggestions([]);
            setSelectedIndex(-1);
        }
    };
    
    // Envia mensagem do formulário
    const handleSend = async (e) => {
        e.preventDefault();
        
        if (!inputValue.trim() || isLoading) {
            return;
        }
        
        setShowSuggestions(false);
        setSuggestions([]);
        
        const message = inputValue.trim();
        setInputValue('');
        await sendMessage(message);
    };
    
    // Expandir para página completa
    const handleExpand = () => {
        if (onExpand) {
            onExpand();
        } else {
            navigate('/assistente-ia');
        }
    };
    
    // Fechar conversa
    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };
    
    // Cleanup do debounce timer
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);
    
    // Handlers para interação (mais simples e direto)
    const handleMouseEnter = () => setIsInteracting(true);
    const handleMouseLeave = () => setIsInteracting(false);
    const handleFocus = () => setIsInteracting(true);
    const handleBlur = (e) => {
        // Usar setTimeout para verificar se o foco mudou para outro elemento do container
        setTimeout(() => {
            if (containerRef.current) {
                const activeElement = document.activeElement;
                // Se o elemento ativo não está dentro do container, desativa interação
                if (!containerRef.current.contains(activeElement)) {
                    setIsInteracting(false);
                }
            }
        }, 100);
    };
    
    // Handler combinado para o textarea (mostra sugestões e gerencia interação)
    const handleTextareaFocus = () => {
        setIsInteracting(true);
        if (suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };
    
    const handleTextareaBlur = (e) => {
        setTimeout(() => {
            setShowSuggestions(false);
            // Verificar se o foco mudou para outro elemento do container
            if (containerRef.current) {
                const activeElement = document.activeElement;
                if (!containerRef.current.contains(activeElement)) {
                    setIsInteracting(false);
                }
            }
        }, 200);
    };
    
    // Handlers para drag (arrastar)
    const handleDragStart = (e) => {
        // Não iniciar drag se clicar em botões ou inputs
        if (e.target.closest('button') || e.target.closest('input') || e.target.closest('textarea')) {
            return;
        }
        
        // Iniciar drag se clicar no header (área de arrastar)
        if (e.target.closest('.drag-handle-area') || e.target.closest('[data-drag-handle]')) {
            setIsDragging(true);
            const rect = containerRef.current?.getBoundingClientRect();
            if (rect) {
                // Calcular offset do mouse em relação ao container
                const currentX = position.x !== null ? position.x : rect.left;
                const currentY = position.y !== null ? position.y : rect.top;
                dragStartPos.current = {
                    x: e.clientX - currentX,
                    y: e.clientY - currentY
                };
                // Cache das dimensões do container
                containerDimensions.current = {
                    width: rect.width,
                    height: rect.height
                };
            }
            e.preventDefault();
            e.stopPropagation();
        }
    };
    
    // Cache para dimensões do container (evitar getBoundingClientRect a cada movimento)
    const containerDimensions = useRef({ width: 0, height: 0 });
    
    const handleDragMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return;
        
        // Cancelar frame anterior se existir
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        
        // Usar requestAnimationFrame para atualizações suaves
        animationFrameRef.current = requestAnimationFrame(() => {
            if (!containerRef.current) return;
            
            // Calcular dimensões apenas uma vez no início do drag
            if (containerDimensions.current.width === 0) {
                const rect = containerRef.current.getBoundingClientRect();
                containerDimensions.current = {
                    width: rect.width,
                    height: rect.height
                };
            }
            
            let newX = e.clientX - dragStartPos.current.x;
            let newY = e.clientY - dragStartPos.current.y;
            
            // Limitar movimento dentro da viewport
            const maxX = window.innerWidth - containerDimensions.current.width;
            const maxY = window.innerHeight - containerDimensions.current.height;
            
            // Permitir chegar bem próximo da borda direita (máximo 98% da largura da tela)
            const maxAllowedX = window.innerWidth * 0.98 - containerDimensions.current.width;
            
            newX = Math.max(0, Math.min(newX, Math.min(maxX, maxAllowedX)));
            newY = Math.max(0, Math.min(newY, maxY));
            
            // Atualizar posição diretamente no DOM para melhor performance
            if (containerRef.current) {
                containerRef.current.style.left = `${newX}px`;
                containerRef.current.style.top = `${newY}px`;
            }
            
            // Atualizar ref e state (mas sem bloquear o render)
            positionRef.current = { x: newX, y: newY };
            setPosition({ x: newX, y: newY });
        });
    }, [isDragging]);
    
    const handleDragEnd = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            // Resetar cache de dimensões
            containerDimensions.current = { width: 0, height: 0 };
            // Salvar posição no localStorage após um pequeno delay para garantir que o estado foi atualizado
            setTimeout(() => {
                if (positionRef.current.x !== null && positionRef.current.y !== null) {
                    localStorage.setItem('ai_chat_position', JSON.stringify(positionRef.current));
                }
            }, 0);
        }
    }, [isDragging]);
    
    // Salvar posição sempre que ela mudar (após drag)
    useEffect(() => {
        if (!isDragging && position.x !== null && position.y !== null) {
            localStorage.setItem('ai_chat_position', JSON.stringify(position));
        }
    }, [position, isDragging]);
    
    // Função para minimizar/maximizar
    const handleToggleMinimize = () => {
        const newMinimizedState = !isMinimized;
        setIsMinimized(newMinimizedState);
        localStorage.setItem('ai_chat_minimized', newMinimizedState.toString());
    };
    
    // Handlers para redimensionamento
    const handleResizeStart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Se estiver minimizado, expandir automaticamente ao começar a redimensionar
        if (isMinimized) {
            setIsMinimized(false);
            localStorage.setItem('ai_chat_minimized', 'false');
        }
        
        setIsResizing(true);
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            resizeStartPos.current = {
                x: e.clientX,
                y: e.clientY,
                width: dimensions.width || rect.width,
                height: dimensions.height || rect.height
            };
        }
    };
    
    const handleResizeMove = useCallback((e) => {
        if (!isResizing || !containerRef.current) return;
        
        // Cancelar frame anterior se existir
        if (resizeAnimationFrameRef.current) {
            cancelAnimationFrame(resizeAnimationFrameRef.current);
        }
        
        // Usar requestAnimationFrame para atualizações suaves
        resizeAnimationFrameRef.current = requestAnimationFrame(() => {
            if (!containerRef.current) return;
            
            const deltaX = e.clientX - resizeStartPos.current.x;
            const deltaY = e.clientY - resizeStartPos.current.y;
            
            // Calcular novas dimensões
            let newWidth = resizeStartPos.current.width + deltaX;
            let newHeight = resizeStartPos.current.height + deltaY;
            
            // Limites mínimos e máximos
            const minWidth = 300;
            const maxWidth = Math.min(800, window.innerWidth * 0.9);
            const minHeight = 300;
            const maxHeight = window.innerHeight * 0.9;
            
            newWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));
            newHeight = Math.max(minHeight, Math.min(newHeight, maxHeight));
            
            // Atualizar dimensões diretamente no DOM para melhor performance
            if (containerRef.current) {
                containerRef.current.style.width = `${newWidth}px`;
                containerRef.current.style.height = `${newHeight}px`;
            }
            
            // Atualizar state
            setDimensions({ width: newWidth, height: newHeight });
        });
    }, [isResizing]);
    
    const handleResizeEnd = useCallback(() => {
        if (isResizing) {
            setIsResizing(false);
            // Salvar dimensões no localStorage
            setTimeout(() => {
                if (dimensionsRef.current.width !== null && dimensionsRef.current.height !== null) {
                    localStorage.setItem('ai_chat_dimensions', JSON.stringify(dimensionsRef.current));
                }
            }, 0);
        }
    }, [isResizing]);
    
    // Salvar dimensões sempre que elas mudarem (após resize)
    useEffect(() => {
        if (!isResizing && dimensions.width !== null && dimensions.height !== null) {
            localStorage.setItem('ai_chat_dimensions', JSON.stringify(dimensions));
        }
    }, [dimensions, isResizing]);
    
    // Event listeners para resize
    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleResizeMove, { passive: false });
            document.addEventListener('mouseup', handleResizeEnd, { passive: true });
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'nwse-resize';
            
            return () => {
                document.removeEventListener('mousemove', handleResizeMove);
                document.removeEventListener('mouseup', handleResizeEnd);
                document.body.style.userSelect = '';
                document.body.style.cursor = '';
                // Cancelar qualquer frame pendente
                if (resizeAnimationFrameRef.current) {
                    cancelAnimationFrame(resizeAnimationFrameRef.current);
                }
            };
        }
    }, [isResizing, handleResizeMove, handleResizeEnd]);
    
    // Event listeners para drag
    useEffect(() => {
        if (isDragging) {
            // Usar passive: false para melhor performance
            document.addEventListener('mousemove', handleDragMove, { passive: false });
            document.addEventListener('mouseup', handleDragEnd, { passive: true });
            document.body.style.userSelect = 'none'; // Prevenir seleção de texto durante drag
            document.body.style.cursor = 'move';
            document.body.style.pointerEvents = 'auto';
            
            return () => {
                document.removeEventListener('mousemove', handleDragMove);
                document.removeEventListener('mouseup', handleDragEnd);
                document.body.style.userSelect = '';
                document.body.style.cursor = '';
                document.body.style.pointerEvents = '';
                // Cancelar qualquer frame pendente
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                }
            };
        }
    }, [isDragging, handleDragMove, handleDragEnd]);
    
    // Opacidade dinâmica: 
    // - Quando interagindo: 100% opaco (0% transparência) = totalmente branco
    // - Quando não interagindo: 65% opaco (transparência MÉDIA = 35% transparente) = semi-transparente
    const opacityClass = isInteracting ? 'bg-white' : 'bg-white/65';
    
    // Estilos inline para posição e dimensões
    const positionStyle = position.x !== null && position.y !== null 
        ? { 
            position: 'fixed',
            left: `${position.x}px`,
            top: `${position.y}px`,
            right: 'auto',
            marginTop: 0,
            width: dimensions.width ? `${dimensions.width}px` : '90vw',
            maxWidth: dimensions.width ? `${dimensions.width}px` : '420px',
            height: isMinimized ? 'auto' : (dimensions.height ? `${dimensions.height}px` : undefined),
            willChange: isDragging || isResizing ? 'left, top, width, height' : 'auto', // Otimização para GPU
            transform: isDragging || isResizing ? 'translateZ(0)' : 'none', // Forçar aceleração de hardware
            zIndex: 60
          }
        : {
            width: dimensions.width ? `${dimensions.width}px` : '100%',
            maxWidth: dimensions.width ? `${dimensions.width}px` : '100%',
            height: isMinimized ? 'auto' : (dimensions.height ? `${dimensions.height}px` : undefined)
          };
    
    return (
        <div 
            ref={containerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`flex flex-col ${opacityClass} backdrop-blur-md ${isMinimized ? 'h-auto max-h-none' : dimensions.height ? '' : 'h-[400px] sm:h-[500px] max-h-[70vh] sm:max-h-[80vh]'} ${isDragging || isResizing ? 'cursor-move select-none' : 'transition-all duration-300'} ${position.x !== null ? 'rounded-xl shadow-lg border border-gray-200/50 relative' : ''}`}
            style={{
                ...positionStyle,
                transition: isDragging || isResizing ? 'none' : undefined // Remover transição durante drag ou resize
            }}
            tabIndex={-1}
        >
            {/* Header Compacto */}
            <div 
                className={`bg-gradient-to-r ${isInteracting ? 'from-purple-50 to-indigo-50' : 'from-purple-50/65 to-indigo-50/65'} backdrop-blur-sm border-b border-gray-200/50 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between flex-shrink-0 transition-all duration-300 drag-handle-area cursor-move`}
                onMouseDown={handleDragStart}
            >
                <div className="flex items-center gap-2">
                    <div
                        data-drag-handle
                        className="p-1 text-gray-400 hover:text-gray-600 cursor-move transition-colors flex items-center"
                        title="Arrastar para mover"
                    >
                        <GripVertical className="h-4 w-4" />
                    </div>
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900">Higeia IA</h3>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={handleExpand}
                        className={`p-1 sm:p-1.5 text-gray-600 hover:text-gray-900 rounded-lg transition-colors ${isInteracting ? 'hover:bg-white' : 'hover:bg-white/50'}`}
                        title="Abrir em tela cheia"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                    >
                        <Maximize2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </button>
                    <button
                        onClick={handleToggleMinimize}
                        className={`p-1 sm:p-1.5 text-gray-500 hover:text-gray-700 rounded-lg transition-colors ${isInteracting ? 'hover:bg-white' : 'hover:bg-white/50'}`}
                        title={isMinimized ? "Expandir conversa" : "Minimizar conversa"}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                    >
                        {isMinimized ? (
                            <ChevronUp className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        ) : (
                            <Minus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        )}
                    </button>
                    <button
                        onClick={handleClose}
                        className={`p-1 sm:p-1.5 text-gray-500 hover:text-red-600 rounded-lg transition-colors ${isInteracting ? 'hover:bg-white' : 'hover:bg-white/50'}`}
                        title="Fechar conversa"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                    >
                        <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </button>
                </div>
            </div>
            
            {/* Área de Mensagens - Oculto quando minimizado */}
            {!isMinimized && (
                <div className={`flex-1 overflow-y-auto px-3 sm:px-4 py-2 sm:py-3 scroll-smooth transition-all duration-300 ${isInteracting ? 'bg-white' : 'bg-white/65'}`} style={{ minHeight: 0 }}>
                {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center px-2">
                            <p className="text-xs sm:text-sm text-gray-500 max-w-xs">
                                Digite sua mensagem abaixo para começar a conversar com o assistente de IA.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-1.5 sm:space-y-2">
                        {messages.map((message) => (
                            <MessageBubble
                                key={message.id}
                                role={message.role}
                                content={message.content}
                                timestamp={message.timestamp}
                                type={message.type}
                            />
                        ))}
                        {isLoading && (
                            <div className="flex justify-start my-1.5 sm:my-2">
                                <div className="bg-white border border-gray-200 rounded-xl px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-sm">
                                    <div className="flex items-center space-x-1.5 sm:space-x-2">
                                        <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                                        <span className="text-[10px] sm:text-xs text-gray-500 animate-pulse">
                                            Digitando...
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>
            )}
            
            {/* Input de Mensagem - Oculto quando minimizado */}
            {!isMinimized && (
                <div className={`${isInteracting ? 'bg-white' : 'bg-white/65'} backdrop-blur-sm border-t border-gray-200/50 px-3 sm:px-4 py-2 sm:py-3 flex-shrink-0 transition-all duration-300`}>
                <form onSubmit={handleSend} className="flex items-center space-x-1.5 sm:space-x-2">
                    <div className="flex-1 relative">
                        <textarea
                            ref={inputRef}
                            value={inputValue}
                            onChange={handleInputChange}
                            onFocus={handleTextareaFocus}
                            onBlur={handleTextareaBlur}
                            onKeyDown={(e) => {
                                if (showSuggestions && suggestions.length > 0) {
                                    if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        setSelectedIndex(prev => 
                                            prev < suggestions.length - 1 ? prev + 1 : prev
                                        );
                                    } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                                    } else if (e.key === 'Enter' && selectedIndex >= 0) {
                                        e.preventDefault();
                                        insertSuggestion(suggestions[selectedIndex]);
                                    } else if (e.key === 'Escape') {
                                        setShowSuggestions(false);
                                    } else if (e.key === 'Enter' && !e.shiftKey && selectedIndex === -1) {
                                        e.preventDefault();
                                        handleSend(e);
                                    }
                                } else if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend(e);
                                }
                            }}
                            placeholder="Digite sua mensagem..."
                            rows={1}
                            className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-2xl focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                            style={{ minHeight: '36px', maxHeight: '90px' }}
                            disabled={isLoading}
                        />
                        
                        {/* Dropdown de sugestões */}
                        {showSuggestions && suggestions.length > 0 && (
                            <div 
                                ref={suggestionsRef}
                                className="absolute bottom-full left-0 mb-1.5 sm:mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px] sm:min-w-[180px] max-w-[200px] sm:max-w-[250px]"
                            >
                                {suggestions.map((suggestion, index) => (
                                    <button
                                        key={`${suggestion.tipo}-${suggestion.id}`}
                                        type="button"
                                        onClick={() => insertSuggestion(suggestion)}
                                        className={`w-full text-left px-2 py-1.5 hover:bg-emerald-50 transition-colors flex items-center space-x-1.5 sm:space-x-2 ${
                                            index === selectedIndex ? 'bg-emerald-100' : ''
                                        } ${index > 0 ? 'border-t border-gray-100' : ''}`}
                                    >
                                        <img
                                            src={suggestion.fotoUrl || '/images/higeia-avatar-shield.png'}
                                            alt={suggestion.nome_completo}
                                            className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                            onError={(e) => {
                                                e.target.src = '/images/higeia-avatar-shield.png';
                                            }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] sm:text-xs text-gray-900 font-medium truncate">
                                                {suggestion.nome_completo}
                                            </div>
                                            {suggestion.role && (
                                                <div className="text-[9px] sm:text-[10px] text-gray-500 truncate">
                                                    {suggestion.role}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        type="submit"
                        disabled={!inputValue.trim() || isLoading}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald-600 text-white rounded-2xl hover:bg-emerald-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex-shrink-0"
                    >
                        {isLoading ? (
                            <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        )}
                    </button>
                </form>
            </div>
            )}
            
            {/* Handle de redimensionamento no canto inferior direito */}
            {position.x !== null && (
                <div
                    onMouseDown={handleResizeStart}
                    className={`absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize flex items-center justify-center rounded-tl-lg transition-colors ${isInteracting ? 'bg-gray-200 hover:bg-gray-300' : 'bg-gray-200/50 hover:bg-gray-300/70'} ${isResizing ? (isInteracting ? 'bg-gray-400' : 'bg-gray-400/80') : ''}`}
                    title="Redimensionar"
                >
                    {/* Ícone de seta diagonal para redimensionamento */}
                    <svg 
                        className="h-4 w-4 text-gray-600 rotate-90" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor" 
                        strokeWidth="2.5"
                    >
                        <path d="M7 17L17 7M17 7H7M17 7V17" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
            )}
        </div>
    );
};

export default ChatInterfaceMini;

